import React, { useEffect, useMemo, useRef, useState } from "react";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import AddHomeWorkOutlinedIcon from "@mui/icons-material/AddHomeWorkOutlined";
import AddCardOutlinedIcon from "@mui/icons-material/AddCardOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CadastroMedicoModal from "../Modals/CadastroMedicoModal/index.tsx";
import CadastroHospitalModal from "../Modals/CadastroHospitalModal/index.tsx";
import CadastroInstrumentadorModal from "../Modals/CadastroInstrumentadorModal/index.tsx";
import CadastroPacienteModal from "../Modals/CadastroPacienteModal/index.tsx";
import CadastroConvenioModal from "../Modals/CadastroConvenioModal/index.tsx";
import CadastroTipoCirurgiaModal from "../Modals/CadastroTipoCirurgiaModal/index.tsx";
import { useFeedback } from "../../contexts/FeedbackContext.tsx";
import { Response } from "../../types/Api.ts";
import { Medico } from "../../models/Medico.ts";
import { Paciente } from "../../models/Paciente.ts";
import { Instrumentador } from "../../models/Instrumentador.ts";
import { Hospital } from "../../models/Hospital.ts";
import { Convenio } from "../../models/Convenio.ts";
import { TipoCirurgia } from "../../models/TipoCirurgia.ts";
import handleService from "../../services/apiService.ts";
import { MethodsEnum } from "../../enums/Methods.ts";
import { usePlanLimits } from "../../hooks/usePlanLimits.ts";
import { useCirurgiaOpcoes, Entidade } from "../../hooks/useCirurgiaOpcoes.ts";
import FormField from "../../ui/FormField.tsx";
import Button from "../../ui/Button.tsx";
import ErrorBanner from "../../ui/ErrorBanner.tsx";
import { emitirNotificacao } from "../../contexts/NotificationContext.tsx";
import { formatarDataCirurgia } from "../../utils/relativeTime.util.ts";
import "../../styles/design-system.css";

// Nome legivel de cada lista de opcoes, pra mensagem de erro dizer o que
// exatamente nao carregou em vez de um "erro ao carregar dados" generico.
const ROTULO_OPCAO: Record<string, string> = {
  medicos: "médicos",
  pacientes: "pacientes",
  hospitais: "hospitais",
  instrumentadores: "instrumentadores",
  convenios: "convênios",
  tipoCirurgias: "tipos de cirurgia",
};

interface NovaCirurgiaFormProps {
  onSuccess: () => void;
  /** A Drawer que hospeda este form fica com o rodapé (Cancelar/Agendar) fixo,
   * fora da área que rola — então quem controla o estado de "enviando..." desse
   * rodapé, e o próprio botão Cancelar, é o componente pai, não este form. */
  onSubmittingChange?: (submitting: boolean) => void;
}

// Teto do sistema: a Cirurgia tem 3 colunas fixas de instrumentador no banco.
// Quantos a clínica realmente usa vem de Configurações > Empresa, por
// planLimits.maxInstrumentadores — este valor é só o limite superior e o
// fallback enquanto essa informação não chegou.
const MAX_INSTRUMENTADORES_SISTEMA = 3;

const hojeISO = () => new Date().toISOString().slice(0, 10);

/** Multi-select com chips removíveis — equivalente ao Autocomplete multiple do
 * MUI, mas no design system próprio da tela (evita misturar tema MUI padrão
 * claro dentro de um drawer escuro sem ThemeProvider dedicado). */
const InstrumentadoresField = ({
  opcoes,
  selecionados,
  onChange,
  maximo,
}: {
  opcoes: Entidade[];
  selecionados: string[];
  onChange: (ids: string[]) => void;
  maximo: number;
}) => {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const aoClicarFora = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener("mousedown", aoClicarFora);
    return () => document.removeEventListener("mousedown", aoClicarFora);
  }, [aberto]);

  const disponiveis = useMemo(
    () =>
      opcoes.filter(
        (o) => !selecionados.includes(o.id) && o.nome.toLowerCase().includes(busca.toLowerCase())
      ),
    [opcoes, selecionados, busca]
  );

  const nomeDe = (id: string) => opcoes.find((o) => o.id === id)?.nome || id;
  const cheio = selecionados.length >= maximo;

  return (
    <div className="ds-multiselect" ref={boxRef}>
      <div className="ds-multiselect-box" onClick={() => !cheio && setAberto(true)}>
        {selecionados.map((id) => (
          <span className="ds-multiselect-chip" key={id}>
            {nomeDe(id)}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(selecionados.filter((s) => s !== id)); }}
              aria-label={`Remover ${nomeDe(id)}`}
            >
              <CloseIcon style={{ fontSize: 13 }} />
            </button>
          </span>
        ))}
        {!cheio && (
          <input
            type="text"
            value={busca}
            placeholder={
              selecionados.length === 0
                ? maximo === 1
                  ? "Selecione o instrumentador..."
                  : `Selecione até ${maximo} instrumentadores...`
                : ""
            }
            onChange={(e) => { setBusca(e.target.value); setAberto(true); }}
            onFocus={() => setAberto(true)}
          />
        )}
      </div>
      {aberto && !cheio && (
        <div className="ds-multiselect-dropdown">
          {disponiveis.length === 0 ? (
            <div className="ds-multiselect-empty">Nenhum instrumentador encontrado</div>
          ) : (
            disponiveis.map((o) => (
              <button
                type="button"
                key={o.id}
                className="ds-multiselect-option"
                onClick={() => { onChange([...selecionados, o.id]); setBusca(""); }}
              >
                {o.nome}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const NovaCirurgiaForm: React.FC<NovaCirurgiaFormProps> = ({ onSuccess, onSubmittingChange }) => {
  const { showFeedback } = useFeedback();
  const { limits: planLimits, isLoading: planLimitsCarregando } = usePlanLimits();

  const { opcoes, carregando, falhas, adicionarLocal, refetch } = useCirurgiaOpcoes();

  // Quantos instrumentadores esta clínica usa por cirurgia. Cai no teto do
  // sistema enquanto /plano/limites não respondeu, para não piscar um campo
  // a menos e confundir quem está preenchendo.
  const maxInstrumentadores = Math.min(
    planLimits?.maxInstrumentadores || MAX_INSTRUMENTADORES_SISTEMA,
    MAX_INSTRUMENTADORES_SISTEMA
  );
  const { medicos, pacientes, hospitais, instrumentadores, convenios, tipoCirurgias } = opcoes;

  const [medicoModalOpen, setMedicoModalOpen] = useState(false);
  const [hospitalModalOpen, setHospitalModalOpen] = useState(false);
  const [instrumentadorModalOpen, setInstrumentadorModalOpen] = useState(false);
  const [pacienteModalOpen, setPacienteModalOpen] = useState(false);
  const [convenioModalOpen, setConvenioModalOpen] = useState(false);
  const [tipoCirurgiaModalOpen, setTipoCirurgiaModalOpen] = useState(false);

  const [dados, setDados] = useState({
    medico: "",
    paciente: "",
    hospital: "",
    instrumentadores: [] as string[],
    convenio: "",
    tipoCirurgia: "",
    data: hojeISO(),
    horario: "",
    observacao: "",
  });
  const [erros, setErros] = useState<Record<string, string>>({});

  const set = (campo: string, valor: string | string[]) => setDados((prev) => ({ ...prev, [campo]: valor }));

  const hasEmailNotification = planLimits?.notificarEmail ?? false;

  const validar = () => {
    const novosErros: Record<string, string> = {};
    if (!dados.medico) novosErros.medico = "Selecione o médico";
    if (!dados.paciente) novosErros.paciente = "Selecione o paciente";
    if (!dados.hospital) novosErros.hospital = "Selecione o hospital";
    if (dados.instrumentadores.length === 0) novosErros.instrumentadores = "Selecione ao menos um instrumentador";
    if (!dados.tipoCirurgia) novosErros.tipoCirurgia = "Selecione o tipo de cirurgia";
    if (!dados.data) novosErros.data = "Informe a data";
    if (!dados.horario) novosErros.horario = "Informe o horário";
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validar()) return;

    const payload = {
      medico: dados.medico,
      paciente: dados.paciente,
      hospital: dados.hospital,
      instrumentador1: dados.instrumentadores[0],
      instrumentador2: dados.instrumentadores[1] || null,
      instrumentador3: dados.instrumentadores[2] || null,
      convenio: dados.convenio || null,
      tipoCirurgia: dados.tipoCirurgia,
      data: `${dados.data}T${dados.horario}:00`,
      observacao: dados.observacao || null,
    };

    try {
      onSubmittingChange?.(true);
      const result = await handleService({ method: MethodsEnum.POST, endpoint: "/cirurgia", data: payload });
      showFeedback(result.message, "success");
      if (result?.data?.id) {
        emitirNotificacao({
          id: `cirurgia-${result.data.id}-agendada`,
          tipo: "cirurgia_agendada",
          titulo: "Cirurgia agendada",
          descricao: `Cirurgia de ${result.data.paciente} marcada para ${formatarDataCirurgia(result.data.data)} — ${result.data.hospital}.`,
          link: "/cirurgias",
        });
      }
      onSuccess();
    } catch (error: any) {
      // Sem prefixo: o backend já manda a frase pronta e explicativa ("Dr X já
      // tem cirurgia marcada para..."). Concatenar virava "Erro ao cadastrar
      // cirurgia, Dr X já tem...".
      showFeedback(error?.message || "Erro ao cadastrar cirurgia.", "error");
    } finally {
      onSubmittingChange?.(false);
    }
  };

  if (carregando || planLimitsCarregando) {
    // Espera também usePlanLimits (não só useCirurgiaOpcoes) antes de mostrar
    // o formulário: o banner de notificação por e-mail abaixo depende de
    // planLimits e nasce sempre visível (planLimits ainda é null aqui) até essa
    // chamada terminar. Se o formulário já estivesse na tela nesse meio-tempo,
    // o banner podia sumir de repente (empresa com plano Pró+) e empurrar todo
    // o layout pra cima — o suficiente pra fechar qualquer <select> nativo
    // aberto no momento (o navegador fecha popups nativos quando o elemento
    // âncora se desloca). Instrumentadores não é afetado por não ser um select
    // nativo.
    return <p style={{ color: "var(--ink-faint)", padding: "20px 0" }}>Carregando dados...</p>;
  }

  // Antes, uma listagem que falhasse virava silenciosamente um <select> vazio —
  // o usuario ficava sem entender por que "não tem nenhum médico cadastrado".
  // Melhor barrar o formulário e deixar tentar de novo do que deixar agendar
  // com meia dúzia de campos impossíveis de preencher.
  if (falhas.length > 0) {
    return (
      <div style={{ padding: "20px 0", display: "flex", flexDirection: "column", gap: 16, alignItems: "flex-start" }}>
        <ErrorBanner
          message={`Não foi possível carregar ${falhas.map((f) => ROTULO_OPCAO[f]).join(", ")}. Sem esses dados o agendamento não pode ser preenchido corretamente.`}
        />
        <Button type="button" variant="primary" onClick={() => { refetch(); }}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <form id="nova-cirurgia-form" onSubmit={onSubmit}>
      {!hasEmailNotification && (
        <div className="ds-hint-banner" style={{ marginTop: 0, marginBottom: 22 }}>
          <span>🔒 Notificações por e-mail estão disponíveis no plano Agenda Pró. Faça um upgrade para ativar.</span>
        </div>
      )}

      <div className="ds-form-section">
        <div className="ds-form-section-head">
          <span className="num">1</span>
          <h4>Dados da cirurgia</h4>
        </div>
        <div className="ds-form-grid">
          <FormField label="Data *" error={erros.data}>
            <input type="date" value={dados.data} onChange={(e) => set("data", e.target.value)} />
          </FormField>
          <FormField label="Horário *" error={erros.horario}>
            <input type="time" value={dados.horario} onChange={(e) => set("horario", e.target.value)} />
          </FormField>
          <FormField label="Tipo de cirurgia *" span2 error={erros.tipoCirurgia}>
            <div className="ds-field-embed">
              <select value={dados.tipoCirurgia} onChange={(e) => set("tipoCirurgia", e.target.value)}>
                <option value="">Selecione...</option>
                {tipoCirurgias.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
              <button type="button" className="ds-icon-add-btn" onClick={() => setTipoCirurgiaModalOpen(true)} aria-label="Novo tipo de cirurgia">
                <AddCardOutlinedIcon fontSize="small" />
              </button>
            </div>
          </FormField>
        </div>
      </div>

      <div className="ds-form-section">
        <div className="ds-form-section-head">
          <span className="num">2</span>
          <h4>Paciente</h4>
        </div>
        <div className="ds-form-grid ds-form-grid-7030">
          <FormField label="Paciente *" error={erros.paciente}>
            <div className="ds-field-embed">
              <select value={dados.paciente} onChange={(e) => set("paciente", e.target.value)}>
                <option value="">Selecione...</option>
                {pacientes.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
              <button type="button" className="ds-icon-add-btn" onClick={() => setPacienteModalOpen(true)} aria-label="Novo paciente">
                <PersonAddOutlinedIcon fontSize="small" />
              </button>
            </div>
          </FormField>
          <FormField label="Convênio" optional>
            <div className="ds-field-embed">
              <select value={dados.convenio} onChange={(e) => set("convenio", e.target.value)}>
                <option value="">Nenhum</option>
                {convenios.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
              <button type="button" className="ds-icon-add-btn" onClick={() => setConvenioModalOpen(true)} aria-label="Novo convênio">
                <AddCardOutlinedIcon fontSize="small" />
              </button>
            </div>
          </FormField>
        </div>
      </div>

      <div className="ds-form-section">
        <div className="ds-form-section-head">
          <span className="num">3</span>
          <h4>Equipe</h4>
        </div>
        <div className="ds-form-grid">
          <FormField label="Médico *" span2 error={erros.medico}>
            <div className="ds-field-embed">
              <select value={dados.medico} onChange={(e) => set("medico", e.target.value)}>
                <option value="">Selecione...</option>
                {medicos.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
              </select>
              <button type="button" className="ds-icon-add-btn" onClick={() => setMedicoModalOpen(true)} aria-label="Novo médico">
                <PersonAddOutlinedIcon fontSize="small" />
              </button>
            </div>
          </FormField>
          <FormField label="Instrumentadores *" span2 error={erros.instrumentadores}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <InstrumentadoresField
                  opcoes={instrumentadores}
                  selecionados={dados.instrumentadores}
                  onChange={(ids) => set("instrumentadores", ids)}
                  maximo={maxInstrumentadores}
                />
              </div>
              <button type="button" className="ds-icon-add-btn" onClick={() => setInstrumentadorModalOpen(true)} aria-label="Novo instrumentador">
                <GroupAddOutlinedIcon fontSize="small" />
              </button>
            </div>
          </FormField>
        </div>
      </div>

      <div className="ds-form-section">
        <div className="ds-form-section-head">
          <span className="num">4</span>
          <h4>Local</h4>
        </div>
        <div className="ds-form-grid">
          <FormField label="Hospital *" span2 error={erros.hospital}>
            <div className="ds-field-embed">
              <select value={dados.hospital} onChange={(e) => set("hospital", e.target.value)}>
                <option value="">Selecione...</option>
                {hospitais.map((h) => <option key={h.id} value={h.id}>{h.nome}</option>)}
              </select>
              <button type="button" className="ds-icon-add-btn" onClick={() => setHospitalModalOpen(true)} aria-label="Novo hospital">
                <AddHomeWorkOutlinedIcon fontSize="small" />
              </button>
            </div>
          </FormField>
        </div>
      </div>

      <div className="ds-form-section" style={{ marginBottom: 0 }}>
        <div className="ds-form-section-head">
          <span className="num">5</span>
          <h4>Informações adicionais</h4>
        </div>
        <FormField label="Observações" optional>
          <textarea
            rows={3}
            placeholder="Jejum, alergias, exames pendentes..."
            value={dados.observacao}
            onChange={(e) => set("observacao", e.target.value)}
          />
        </FormField>
      </div>

      <CadastroMedicoModal isOpen={medicoModalOpen} onClose={() => setMedicoModalOpen(false)} onAdd={(r: Response<Medico>) => { if (r.data) { adicionarLocal("medicos", r.data); set("medico", r.data.id); } setMedicoModalOpen(false); }} />
      <CadastroHospitalModal isOpen={hospitalModalOpen} onClose={() => setHospitalModalOpen(false)} onAdd={(r: Response<Hospital>) => { if (r.data) { adicionarLocal("hospitais", r.data); set("hospital", r.data.id); } setHospitalModalOpen(false); }} />
      <CadastroInstrumentadorModal isOpen={instrumentadorModalOpen} onClose={() => setInstrumentadorModalOpen(false)} onAdd={(r: Response<Instrumentador>) => { if (r.data) { adicionarLocal("instrumentadores", r.data); if (dados.instrumentadores.length < maxInstrumentadores) set("instrumentadores", [...dados.instrumentadores, r.data.id]); } setInstrumentadorModalOpen(false); }} />
      <CadastroPacienteModal isOpen={pacienteModalOpen} onClose={() => setPacienteModalOpen(false)} onAdd={(r: Response<Paciente>) => { if (r.data) { adicionarLocal("pacientes", r.data); set("paciente", r.data.id); } setPacienteModalOpen(false); }} />
      <CadastroConvenioModal isOpen={convenioModalOpen} onClose={() => setConvenioModalOpen(false)} onAdd={(r: Response<Convenio>) => { if (r.data) { adicionarLocal("convenios", r.data); set("convenio", r.data.id); } setConvenioModalOpen(false); }} />
      <CadastroTipoCirurgiaModal isOpen={tipoCirurgiaModalOpen} onClose={() => setTipoCirurgiaModalOpen(false)} onAdd={(r: Response<TipoCirurgia>) => { if (r.data) { adicionarLocal("tipoCirurgias", { id: r.data.id, nome: r.data.nome }); set("tipoCirurgia", r.data.id); } setTipoCirurgiaModalOpen(false); }} />
    </form>
  );
};

export default NovaCirurgiaForm;
